import React from 'react';
import { useNavigate } from 'react-router-dom';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import Disclaimer from './Disclaimer';
import BrandWordmark, { BRAND_WORDMARK_LABEL as BRAND_NAME } from './BrandWordmark';
import PublicSiteFooter from './PublicSiteFooter';
import { LEGAL_REVIEW_DATE } from './legalDates';
import './PrivacyPolicy.css';

const PRIVACY_HIGHLIGHTS = [
  {
    title: 'Data minimization',
    detail: 'The policy explains what is collected, why it is collected, and how retention is controlled.',
  },
  {
    title: 'Document handling',
    detail: 'Uploaded content is processed to perform redaction, not for advertising or profile building.',
  },
  {
    title: 'User control',
    detail: 'The policy covers deletion requests, support contact, and rights for different jurisdictions.',
  },
];

const PrivacyPolicy = ({ theme = 'dark', toggleTheme }) => {
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  return (
    <div className={`privacy-page-overlay ${isDark ? 'privacy-page-dark' : 'privacy-page-light'}`}>
      <div className="privacy-card">
        <div className="privacy-topbar">
          <div className="privacy-brand"><BrandWordmark className="privacy-brand-mark" /></div>
          <div className="privacy-topbar-actions">
            {toggleTheme ? (
              <button
                className="theme-toggle-btn privacy-theme-toggle"
                type="button"
                onClick={toggleTheme}
                aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              >
                {isDark ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
              </button>
            ) : null}
            <button className="nav-primary-btn privacy-back-btn" type="button" onClick={() => navigate('/')}>
              Back to Home
            </button>
          </div>
        </div>

        <header className="privacy-header">
          <div className="privacy-badge">Legal</div>
          <h1>Redacto Privacy Policy</h1>
          <p className="update-date">Last Updated: {LEGAL_REVIEW_DATE}</p>
        </header>

        <div className="privacy-body privacy-body-readable">
          <Disclaimer>
            <strong>Beta / Free Tier Disclaimer:</strong> Redacto is currently in a FREE launch / beta phase. Billing and paid subscription features are disabled at present. If and when paid subscription features are introduced, we will update this Privacy Policy and provide notice. This launch does not obligate users to any charges.
          </Disclaimer>
          <div className="privacy-hero-grid">
            <div className="privacy-lead-card">
              <div className="privacy-section-kicker">Privacy summary</div>
              <h2>Privacy by design, explained in plain language.</h2>
              <p>
                This policy describes what Redacto collects, how document data is processed, what is retained, and
                how users can manage deletion or support requests.
              </p>
            </div>
            <div className="privacy-summary-stack">
              {PRIVACY_HIGHLIGHTS.map((card) => (
                <article key={card.title} className="privacy-summary-card">
                  <h3>{card.title}</h3>
                  <p>{card.detail}</p>
                </article>
              ))}
            </div>
          </div>
          <p>
            At Redacto, we believe that privacy is not a feature; it is a foundation. Our platform exists to help
            organizations protect sensitive information within documents, and we hold ourselves to the same standards we
            help our users uphold. This Privacy Policy explains in detail who we are, what information we collect, why we
            collect it, how we use it, how we protect it, and what rights you have regarding your personal data.
          </p>
          <p>
            Whether you are a first-time visitor to our website, a registered user of the platform, or an organization
            deploying Redacto at scale, this document is intended to give you a clear and complete picture of our data
            practices. We encourage you to read it carefully, and we welcome any questions you may have.
          </p>

          <h3>1. About Redacto</h3>
          <p>
            Redacto is a secure document redaction platform designed for organizations that need to remove, obscure, or
            transform sensitive information within documents before sharing, archiving, or publishing them. Our tools are
            built for legal, compliance, healthcare, government, and enterprise use cases where confidentiality and
            precision are critical.
          </p>
          <p>
            Redacto operates with a privacy-by-design philosophy. This means that privacy protections are embedded into the
            architecture of the product from the ground up, not applied as an afterthought. Our systems are designed to
            minimize data exposure, limit unnecessary processing, and give organizations meaningful control over how their
            documents and data are handled.
          </p>
          <p>
            Our registered office and primary data controller is Redacto. For all privacy-related inquiries, please contact
            us at partnerships@redacto.net or visit our website at www.redacto.net.
          </p>

          <h4>1.1 Controller and Processor Roles</h4>
          <p>
            For account, billing, support, and website analytics data, {BRAND_NAME} generally acts as controller. For
            customer-uploaded documents processed on behalf of business customers, {BRAND_NAME} generally acts as
            processor/service provider.
          </p>

          <h3>2. Scope of This Policy</h3>
          <p>
            This Privacy Policy applies to all individuals and organizations that interact with {BRAND_NAME} services,
            including:
          </p>
          <ul>
            <li>Visitors who browse the {BRAND_NAME} website (www.redacto.net) without creating an account</li>
            <li>Registered users who have created individual or organizational accounts on the platform</li>
            <li>Organizations that have licensed or deployed {BRAND_NAME} as part of their internal or client-facing workflows</li>
            <li>Individuals whose personal data may appear incidentally within documents uploaded to the platform for redaction</li>
            <li>Partners, vendors, or integrators who connect with {BRAND_NAME} services via API or third-party integration</li>
          </ul>
          <p>
            This policy does not apply to third-party websites, services, or platforms that are not operated or controlled
            by {BRAND_NAME}, even if they are linked to from our website or integrated alongside our services. We encourage you
            to review the privacy policies of any third-party services you use in connection with your work.
          </p>

          <h3>3. Information We Collect</h3>
          <p>
            We collect information to operate and improve the {BRAND_NAME} platform, ensure its security, and comply with
            applicable legal obligations. We collect only what is necessary for these purposes. The categories of
            information we may collect are described below.
          </p>

          <h4>3.1 Account and Identity Information</h4>
          <p>
            When you register for a {BRAND_NAME} account, whether as an individual user or on behalf of an organization, we may
            collect the following:
          </p>
          <ul>
            <li>Full name</li>
            <li>Email address</li>
            <li>Organization or company name (if voluntarily provided)</li>
            <li>Job title or role (if voluntarily provided)</li>
            <li>Login credentials and session authentication data are used to operate account access.</li>
            <li>Billing address and payment information (where applicable, processed through a secure third-party payment provider; billing currently disabled for the free launch)</li>
          </ul>
          <p>
            This information is used strictly for the purposes of authentication, access control, communication, and
            account management. We do not sell, rent, or trade account information with third parties for marketing
            purposes.
          </p>

          <h4>3.2 Document Data</h4>
          <p>
            The core function of {BRAND_NAME} involves processing documents that users upload to the platform. When you upload a
            document, the platform may process:
          </p>
          <ul>
            <li>The text content within the document</li>
            <li>Embedded metadata (such as author fields, creation dates, and revision history)</li>
            <li>File structure and format information (such as PDF structure, XML tags, or embedded objects)</li>
          </ul>
          <p>
            {BRAND_NAME} processes document content exclusively for the purpose of performing redaction and document
            transformation tasks as requested by the user. We do not read, analyze, index, or retain document content for
            any purpose other than completing the task you have initiated.
          </p>
          <p>
            We do not use document content for advertising, product training on identifiable data, or any form of
            profiling. {BRAND_NAME} confirms that by default, document content is not used for training any internal or
            third-party AI models. We contractually prohibit third-party model providers (where involved) from training on
            customer content. Enterprise plans may be configured with restricted-routing or no-retention requirements.
            Documents are treated as confidential by design, and access to document content is strictly controlled and
            limited to automated processing systems except where explicit support is required and authorized.
          </p>

          <h4>3.3 Usage and Technical Data</h4>
          <p>
            In order to maintain the performance, security, and reliability of the {BRAND_NAME} platform, we collect limited
            technical data about how users interact with the service. This may include:
          </p>
          <ul>
            <li>IP address and approximate geographic location (country or region level)</li>
            <li>Browser type and version</li>
            <li>Operating system and device type</li>
            <li>Session timestamps, duration, and activity patterns</li>
            <li>Pages visited and features used within the platform</li>
            <li>Error logs and crash reports</li>
            <li>Performance metrics such as processing times and system load indicators</li>
          </ul>
          <p>
            This data is used internally for the purposes of system security monitoring, detecting and preventing misuse or
            unauthorized access, diagnosing technical issues and bugs, optimizing platform performance, and planning
            infrastructure capacity. We do not use technical usage data to build individual user profiles for commercial
            purposes.
          </p>

          <h4>3.4 Communications Data</h4>
          <p>
            If you contact us through our support channels, inquiry forms, or email, we will retain records of those
            communications to respond to your request and improve our support services. This may include your name, email
            address, the content of your inquiry, and any supporting materials you provide. Communications are stored
            securely and accessed only by authorized {BRAND_NAME} personnel.
          </p>

          <h3>4. How We Use Your Information</h3>
          <p>
            The information we collect is used for specific, legitimate purposes that are directly related to operating and
            improving the {BRAND_NAME} platform. We process your information only when we have a lawful basis for doing so, such
            as contractual necessity, legitimate business interest, legal obligation, or your explicit consent. The primary
            purposes for which we use information are:
          </p>

          <h4>4.1 Providing and Operating Our Services</h4>
          <p>
            We use your account information and document data to authenticate your identity, process uploaded documents,
            deliver redaction results, and maintain the functionality of features you use. Without this processing, we
            would be unable to provide the core services you have requested.
          </p>

          <h4>4.2 Improving and Developing the Platform</h4>
          <p>
            We use aggregated, anonymized usage data to understand how the platform is used, identify areas for
            improvement, and develop new features and capabilities. For example, understanding which redaction modes are
            most frequently used helps us prioritize enhancements to those workflows. We never use identifiable document
            content for this purpose.
          </p>

          <h4>4.3 Security and Fraud Prevention</h4>
          <p>
            We use technical and usage data to detect and prevent unauthorized access, fraud, abuse, and other security
            threats. Our monitoring systems flag unusual activity patterns so that we can investigate and respond to
            potential incidents quickly. Maintaining the security of the platform and the data it processes is one of our
            highest priorities.
          </p>

          <h4>4.4 Communication and Support</h4>
          <p>
            We may use your contact information to send you important notices about your account, updates to this Privacy
            Policy, our Terms of Service, or responses to your support requests. We may also send you product updates or
            announcements if you have opted in to receiving such communications. You can manage your communication
            preferences at any time through your account settings or by contacting us directly.
          </p>

          <h4>4.5 Legal and Compliance Obligations</h4>
          <p>
            We may process and retain your information where required to comply with applicable laws, regulations, court
            orders, or lawful governmental requests. We take care to ensure that such disclosures are limited to what is
            strictly required by law and that we notify you where permitted before making such disclosures.
          </p>

          <h3>5. Document Processing and Data Retention</h3>
          <p>
            Because {BRAND_NAME} handles sensitive documents, we have designed specific policies around how documents are stored,
            processed, and deleted. Our approach is guided by the principle of data minimization; we retain data only for
            as long as it is necessary to fulfill the purpose for which it was collected.
          </p>


          <h4>5.1 Storage During Use</h4>
          <p>
            When a document is uploaded to {BRAND_NAME}, it is stored on the {BRAND_NAME} server to enable processing, preview,
            download, and related workflow operations.
          </p>

          <h4>5.2 Deletion and Retention</h4>
          <p>
            Files are stored only as needed for processing and review. For the current FREE launch, {BRAND_NAME} will retain
            uploaded files for up to 30 days from the time of upload unless the user deletes them earlier through the
            product interface or requests deletion via support. Users may request immediate deletion at any time by
            contacting partnerships@redacto.net. Enterprise retention settings and custom retention policies are available
            for supported paid deployments.
          </p>

          <h4>5.3 Retention Criteria</h4>
          <p>
            We retain data for the period necessary to fulfill the purpose for which it was collected, or as required by
            law. The criteria used to determine storage periods vary by data category, including: Customer-Uploaded
            Documents (Source/Redacted Output Files) retained until deleted by the user or by approved support request, and
            in some deployments may also be subject to configured retention windows; Account Data retained for the duration of the active account;
            Billing/Transaction Data retained for the period required to comply with financial and tax laws; Technical Logs
            (Usage, Security, Performance) retained for a limited, defined period; and Support Communications retained as
            needed to resolve the inquiry and improve service quality.
          </p>

          <h4>5.4 Deletion Requests</h4>
          <p>
            You may request deletion of your account and associated data at any time by contacting us at
            partnerships@redacto.net. We will process deletion requests promptly and confirm when deletion has been
            completed. Please note that some information may remain in backup systems for a limited period before
            being fully purged in accordance with our backup rotation schedule.
          </p>

          <h3>6. Data Security</h3>
          <p>
            {BRAND_NAME} implements multiple technical and organizational security measures to protect the information we hold.
            We continuously evaluate and update our security practices in response to evolving threats and industry
            standards.
          </p>

          <h4>6.1 Access Controls</h4>
          <p>
            Access to data within the {BRAND_NAME} platform is controlled through account/session authentication and ownership
            checks in application workflows. Administrative access to underlying systems is limited to authorized personnel
            and service components based on operational need. Audit logging currently captures key workflow events (for
            example upload, redact, download, delete, and login), rather than every possible access event.
          </p>

          <h4>6.2 Data Transmission Security</h4>
          <p>Data is intended to be transmitted over HTTPS/TLS in production deployments. In local/development environments, unencrypted HTTP may be used.</p>

          <h4>6.3 File Isolation</h4>
          <p>
            Documents uploaded by one user or organization are separated from those of other users or organizations
            through ownership checks and per-user path separation. Our
            system architecture is designed to prevent cross-tenant data access through ownership checks and path
            separation. File processing is handled by application workflows and supporting services; deployment-specific
            isolation controls may vary by environment.
          </p>

          <h4>6.4 Incident Response and Security Contact</h4>
          <p>
            {BRAND_NAME} maintains a robust incident response program. In the event of a confirmed data breach impacting user
            data, we commit to notifying affected users as required by law and contract. For security concerns or to report
            a vulnerability, please contact us immediately. We also provide an enterprise security review process for
            qualified partners.
          </p>

          <h3>7. Sharing and Disclosure of Information</h3>
          <p>
            {BRAND_NAME} does not sell user data. We do not share document content for advertising, analytics, or commercial
            purposes. We share information only in the limited circumstances described below.
          </p>

          <h4>7.1 Infrastructure and Service Providers</h4>
          <p>
            We work with trusted infrastructure providers, cloud hosting services, and supporting technology vendors that
            help us operate the {BRAND_NAME} platform. These providers access information only to the extent necessary to provide
            their services and are bound by contractual obligations to protect it and use it only for agreed-upon purposes.
            We do not share document content with third-party service providers except where it is strictly necessary to
            complete the processing you have requested.
          </p>
          <p>
            This may include payment processors (such as Stripe, when billing is enabled), identity providers (such as
            Google, when sign-in or integrations are used), and infrastructure/hosting providers required to operate
            {BRAND_NAME}.
          </p>

          <h4>7.2 Legal Requirements</h4>
          <p>
            We may disclose personal information to law enforcement agencies, courts, or government authorities where
            required by applicable law, regulation, or legal process. We will assess such requests carefully and disclose
            only the minimum information required. Where permitted by law, we will notify affected users of such requests
            before complying.
          </p>

          <h4>7.4 International Data Transfers</h4>
          <p>
            If EU/UK/Swiss personal data is processed in the U.S. or other jurisdictions, {BRAND_NAME} relies on Standard
            Contractual Clauses (SCCs) or another lawful transfer mechanism where required by applicable contracts and law.
            Transfer mechanism documentation is available upon request for applicable enterprise engagements.
          </p>

          <h3>8. Compliance and Regulatory Alignment</h3>
          <p>
            {BRAND_NAME} is built to support privacy-conscious workflows, but each organization remains responsible for
            determining and meeting the legal and regulatory requirements applicable to its own use of the platform. Our
            platform architecture and data practices are designed with the following regulatory considerations in mind:
          </p>
          <ul>
            <li>General Data Protection Regulation (GDPR), applicable to users and data subjects within the European Economic Area</li>
            <li>Health Insurance Portability and Accountability Act (HIPAA), relevant for healthcare organizations processing protected health information</li>
            <li>Data protection and privacy laws applicable in the jurisdictions where our users operate</li>
            <li>Industry-specific confidentiality and information security standards</li>
          </ul>
          <p>
            If your organization has specific compliance requirements, we encourage you to contact us at
            partnerships@redacto.net to discuss how {BRAND_NAME} can best support your needs. We can provide
            documentation, including a Data Processing Addendum (DPA), in applicable commercial arrangements.
          </p>

          <h3>9. Your Rights as a Data Subject</h3>
          <p>
            Depending on your jurisdiction and applicable law, you may have rights with respect to your personal data.
            {BRAND_NAME} is committed to enabling you to exercise these rights in a timely and straightforward manner. The rights
            available to you may include:
          </p>

          <h4>9.1 Right of Access</h4>
          <p>
            You have the right to request a copy of the personal data we hold about you, along with information about how
            it is being used.
          </p>

          <h4>9.2 Right to Rectification</h4>
          <p>
            If the personal data we hold about you is inaccurate or incomplete, you have the right to request that we
            correct or update it.
          </p>

          <h4>9.3 Right to Erasure</h4>
          <p>
            You have the right to request that we delete your personal data when it is no longer necessary for the purpose
            for which it was collected, or when you withdraw your consent on which processing was based.
          </p>

          <h4>9.4 Right to Data Portability</h4>
          <p>
            Where processing is based on your consent or is necessary to perform a contract, you may have the right to
            receive your personal data in a structured, commonly used, machine-readable format, and to transmit it to
            another controller.
          </p>

          <h4>9.5 Right to Restriction of Processing</h4>
          <p>
            In certain circumstances, you have the right to request that we restrict the processing of your personal data,
            for example while you are contesting its accuracy or the lawfulness of our processing.
          </p>

          <h4>9.6 Right to Object</h4>
          <p>
            You have the right to object to the processing of your personal data where it is based on our legitimate
            interests. We will cease such processing unless we can demonstrate compelling legitimate grounds that override
            your interests.
          </p>

          <h4>9.7 Right to Withdrawal of Consent</h4>
          <p>
            Where processing is based on your consent, you have the right to withdraw that consent at any time, without
            affecting the lawfulness of processing based on consent before its withdrawal.
          </p>

          <h4>9.8 Right to Lodge a Complaint</h4>
          <p>
            You have the right to lodge a complaint with a supervisory authority, particularly in the Member State of your
            habitual residence, place of work, or place of the alleged infringement.
          </p>

          <h3>10. User Responsibilities</h3>
          <p>
            While {BRAND_NAME} is designed to be a powerful tool for protecting sensitive information, effective data governance
            is a shared responsibility. As a user of the platform, you are responsible for:
          </p>
          <ul>
            <li>Uploading only documents that you are authorized to process and that your organization has the right to submit to a third-party platform</li>
            <li>Ensuring that your organization&apos;s use of {BRAND_NAME} complies with applicable data protection and confidentiality laws</li>
            <li>Configuring the platform appropriately for your specific compliance requirements</li>
            <li>Managing user access within your organization&apos;s {BRAND_NAME} instance, including promptly revoking access for individuals who no longer require it</li>
            <li>Safeguarding your login credentials and reporting any suspected unauthorized access immediately</li>
          </ul>
          <p>
            {BRAND_NAME} provides robust tools and controls to support responsible document handling, but the policy and
            governance decisions around which documents are processed, by whom, and under what conditions remain within your
            organization&apos;s control.
          </p>

          <h3>11. Cookies and Session Data</h3>
          <p>
            {BRAND_NAME} uses essential cookies and server-side session data to maintain authentication state, secure requests,
            and preserve in-product workflow state. These are required for core platform functionality.
          </p>

          <h3>12. Children&apos;s Privacy</h3>
          <p>
            {BRAND_NAME} is a professional platform designed for business and organizational use. Our services are not directed
            at children, and we do not knowingly collect personal information from children under 13. Where a higher age
            threshold applies under local law, we honor that threshold. If we become aware that personal data from a child
            has been collected, we will take prompt steps to delete such data. If you believe a child&apos;s data may have been
            submitted to our platform, please contact us immediately at partnerships@redacto.net.
          </p>

          <h3>13. Changes to This Privacy Policy</h3>
          <p>
            {BRAND_NAME} may update this Privacy Policy from time to time to reflect changes to our platform, data practices, or
            applicable legal requirements. When we make significant changes, we will provide notice through one or more of
            the following means:
          </p>
          <ul>
            <li>Posting an updated version of this policy on our website with a revised "Last Updated" date</li>
            <li>Sending an email notification to registered users at the address associated with their account</li>
            <li>Displaying a prominent in-platform notice drawing attention to the changes</li>
          </ul>
          <p>
            We encourage you to review this policy periodically to stay informed about how we protect your information. Your
            continued use of the {BRAND_NAME} platform following the posting of changes constitutes your acknowledgment of the
            updated policy.
          </p>

          <h3>14. Contact Information</h3>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy, the data we hold about you, or
            our privacy practices in general, please do not hesitate to contact us:
          </p>
          <p>Email: partnerships@redacto.net</p>
          <p>Website: www.redacto.net</p>
          <p>
            We are committed to addressing your concerns fairly and promptly, and to working with you and, where
            applicable, with relevant data protection authorities to resolve any disputes regarding the processing of your
            personal data.
          </p>

          <p>© {BRAND_NAME}. All rights reserved. | www.redacto.net | partnerships@redacto.net</p>
        </div>

        <footer className="privacy-footer">
          <button className="close-privacy-btn" onClick={() => window.history.back()}>
            I Understand
          </button>
        </footer>
      </div>
      <PublicSiteFooter />
    </div>
  );
};

export default PrivacyPolicy;
